'use strict';

/**
 * Generates optimized image URLs pointing to the public CloudFront CDN.
 *
 * The URL convention matches the S3 key structure used by imageStorage.js:
 *   {CDN_BASE}/media/{type}/{entityId}/{imageIndex}/{variantName}.webp
 *
 * Usage:
 *   import { getOptimizedImageUrl, getItemSrcSet } from '@src/utils/getOptimizedImageUrl';
 *
 *   // Single URL
 *   getOptimizedImageUrl('item', itemId, 0, 'thumb')
 *   // → "https://media.task-marketplace.bcaa.cloud/media/items/abc-123/0/thumb.webp"
 *
 *   // srcset for responsive images
 *   getItemSrcSet(itemId, 0)
 *   // → "https://cdn.../thumb.webp 400w, https://cdn.../medium.webp 800w"
 */

function getCdnBase() {
    const url = getEnvVar('AWS_CLOUDFRONT_MEDIA_URL');
    if (!url) {
        return null;
    };

    return url.replace(/\/$/, '');
}

function getEnvVar(name) {
    if (typeof window !== 'undefined' && window.__ENV__?.[name]) {
        return window.__ENV__[name];
    }

    // Server-side: process.env is available via Node
    if (typeof process !== 'undefined' && process.env) {
        return process.env[name];
    }

    return undefined;
}

/**
 * Build the optimized image URL for a given entity and variant.
 *
 * @param {string} type - Entity type: 'item', 'category', 'banner', 'avatar'
 * @param {string} entityId - Entity ID (ignored for 'banner' type)
 * @param {number} imageIndex - Image index (0-based)
 * @param {string} variantName - Variant: 'thumb', 'medium', 'full', 'mobile', 'desktop'
 * @returns {string|null} Full CDN URL, or null if CDN is not configured
 */
export function getOptimizedImageUrl(type, entityId, imageIndex, variantName) {
    const base = getCdnBase();

    if (!base) {
        return null;
    }

    // Banners use a fixed "manual" path since they are processed manually
    if (type === 'banner') {
        return `${base}/media/banner/manual/${imageIndex}/${variantName}.webp`;
    }

    if (!entityId) {
        return null;
    }

    return `${base}/media/${type}/${entityId}/${imageIndex}/${variantName}.webp`;
}

/**
 * Generate a srcset string for item listing cards.
 * Provides thumb (400w) and medium (800w) variants.
 *
 * @param {string} itemId - Item ID
 * @param {number} imageIndex - Image index (default 0)
 * @returns {string|null} srcset attribute value, or null if CDN not configured
 */
export function getItemSrcSet(itemId, imageIndex = 0) {
    const base = getCdnBase();
    if (!base || !itemId) return null;

    const prefix = `${base}/media/item/${itemId}/${imageIndex}`;
    return `${prefix}/thumb.webp 400w, ${prefix}/medium.webp 800w`;
}

/**
 * Generate a srcset string for banner images (mobile + desktop).
 * Banners use a fixed "manual" path since they are processed manually.
 *
 * @param {string} entityId - Panel/banner detail ID (unused, kept for API compatibility)
 * @param {number} imageIndex - Image index (default 0)
 * @returns {string|null} srcset attribute value, or null if CDN not configured
 */
export function getBannerSrcSet(entityId, imageIndex = 0) {
    const base = getCdnBase();
    if (!base) return null;

    const prefix = `${base}/media/banner/manual/${imageIndex}`;
    return `${prefix}/mobile.webp 768w, ${prefix}/desktop.webp 1920w`;
}

/**
 * Generate a srcset string for category images.
 *
 * @param {string} categoryId - Category ID
 * @param {number} imageIndex - Image index (default 0)
 * @returns {string|null} srcset attribute value, or null if CDN not configured
 */
export function getCategorySrcSet(categoryId, imageIndex = 0) {
    const base = getCdnBase();
    if (!base || !categoryId) {
        return null;
    }

    const prefix = `${base}/media/category/${categoryId}/${imageIndex}`;
    return `${prefix}/thumb.webp 270w, ${prefix}/medium.webp 540w`;
}

/**
 * Image onError handler that falls back to the original Arcadier URL.
 * Prevents infinite loops by checking if we already fell back.
 *
 * @param {Event} event - The img onError event
 * @param {string} fallbackUrl - Original image URL (Arcadier)
 */
export function handleImageError(event, fallbackUrl) {
    const img = event.target;
    // Prevent infinite loop: only fallback once
    if (img.dataset.fellBack) return;
    img.dataset.fellBack = 'true';
    img.src = fallbackUrl;
    // Clear srcset so the browser uses the fallback src
    img.srcset = '';
}
